import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Clock from 'iconsax-react/dist/esm/Clock';
import { computeEffectiveTable, requiresRequestFlow, TDR, toLuxon } from 'tdr-common';
import { useSearch } from '../../api/availability/search';
import { useReservation } from '../../api/getReservation';
import { useRestaurant } from '../../api/getRestaurant';
import { useTables } from '../../api/getRestaurantTables';
import { useTimeslots } from '../../api/getTimeslots';
import { URLParams } from '../../common/types';
import { SquareIconButton } from '../../components/buttons/SquareIconButton';
import { Header } from '../../components/checkout/Header';
import { DiscardChanges } from '../../components/dialog/controlled-dialog/DiscardChanges';
import { NoMatchingTables } from '../../components/dialog/controlled-dialog/NoMatchingTables';
import { AvailableCard } from '../../components/inventory-carousel/AvailableCard';
import { CardShell } from '../../components/inventory-carousel/CardShell';
import { CarouselSkeleton } from '../../components/inventory-carousel/CarouselSkeleton';
import { useModificationFlowState } from '../../context/ModificationFlowProvider';
import { getDisplayTime } from '../../helpers/getDisplayTime';
import { SearchFilters } from '../../routes/AvailabilityGuard';
import { ModifyBookingFormData } from '../../routes/BookingGuard';

function getNextAvailableTime(selectedTime: string, availableTimes: string[]): string {
	const next = availableTimes
		.filter(time => time > selectedTime)
		.sort((a, b) => a.localeCompare(b));
	return next.length ? next[0] : (availableTimes[0] ?? '');
}

const TimeConflictFooter = ({ nextTime, onPickTime }: { nextTime: string; onPickTime: () => void }) => {
	const { t } = useTranslation();
	return (
		<div className="bg-tablz-med-gray-1 flex items-center justify-between p-3 rounded-2xl text-sm">
			<div className="flex gap-1 items-center">
				<Clock size={16} color="#FACC15" variant="Bold" />
				<p className="font-poppins text-yellow-400">
					<Trans
						i18nKey="inventory.cross_sell_time_available"
						values={{ time: getDisplayTime(nextTime) }}
						components={[<span key="b" className="font-semibold" />]}
					/>
				</p>
			</div>
			<button
				type="button"
				className="font-poppins font-semibold text-[color:var(--zinc-200,#e4e4e7)] cursor-pointer"
				onClick={onPickTime}
				data-testid="see-details-btn"
			>
				{t('inventory.see_details')}
			</button>
		</div>
	);
};

export const ChangeSeatingPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { reservationId } = useParams<URLParams>();

	const { data: reservation } = useReservation(reservationId);
	const { data: restaurant } = useRestaurant(reservation.restaurantId);
	const { data: tables, isLoading } = useTables(reservation.restaurantId);

	const { setNewInvoice } = useModificationFlowState();

	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [hasNoMatchingTables, setHasNoMatchingTables] = useState(false);

	const methods = useFormContext<ModifyBookingFormData>();

	const searchFilters: SearchFilters = {
		groupSize: methods.watch('guests'),
		date: toLuxon(methods.watch('epochInSeconds'), restaurant.timezone).toFormat('yyyy-MM-dd'),
		time: toLuxon(methods.watch('epochInSeconds'), restaurant.timezone).toFormat('HH:mm')
	};

	const { results, isLoading: isSearching } = useSearch({
		params: searchFilters,
		restaurantId: restaurant.id,
		space: tables
	});

	const tableIds = tables?.map((tableItem) => tableItem.id) ?? [];
	const { data: bulkTimeslots } = useTimeslots({
		tableIds,
		dateStr: searchFilters.date,
		restaurantId: restaurant.id,
		options: { groupSize: searchFilters.groupSize }
	});
	const bulkTimeslotMap = bulkTimeslots?.timeSlotDocMap;

	const handleItemClick = (item: TDR.Table) => {
		methods.setValue('tableId', item.id, { shouldDirty: true });
		navigate('details');
	};

	const resetFormAndExit = () => {
		methods.reset();
		setNewInvoice(undefined);
		navigate(`/booking/${reservationId}`);
	};

	const handleExitClick = () => {
		if (methods.formState.isDirty) {
			setIsConfirmOpen(true);
		}
		else {
			resetFormAndExit();
		}
	};

	const handleConfirmExit = () => {
		resetFormAndExit();
		setIsConfirmOpen(false);
	};

	const handleCancelExit = () => {
		setIsConfirmOpen(false);
	};

	const handleBackClick = () => {
		navigate(-1);
	};

	// Only show available tables for currently booked time (that are not the currently booked table)
	const searchResultTables = tables.filter(
		(table) => results?.metadata.time.includes(table.id) && table.id !== reservation.tableId
	);

	// Only offer tables that keep the booking on the same side of the instant/request boundary.
	//
	// This mirrors the server guard in updateReservation, which rejects a guest-initiated change that
	// crosses it (BOOKING_TYPE_CHANGED). It must agree with that guard exactly, or the guest picks a
	// table here and hits an error on submit -- so it resolves the candidate table the same way the
	// server does: against the reservation's NEW date and party size, not the table's default. A
	// table that is request-only on Saturdays is a legitimate match for a request booking being
	// moved to a Saturday, and not for one being moved to a Tuesday.
	const newDateTime = toLuxon(methods.watch('epochInSeconds'), restaurant.timezone || 'UTC');
	const matchingTables = searchResultTables.filter((table) => {
		const effectiveTable = computeEffectiveTable(newDateTime, restaurant, table, {
			groupSize: searchFilters.groupSize
		});
		return (
			table.id !== reservation.tableId &&
			requiresRequestFlow(effectiveTable) === !!reservation.isLargeGroup
		);
	});

	useEffect(() => {
		if (isLoading || isSearching) {
			return;
		}

		if (matchingTables.length === 0) {
			setHasNoMatchingTables(true);
		}
	}, [matchingTables, isLoading, isSearching]);

	return (
		<>
			<div className="sticky top-0 z-10">
				<Header
					title={t('titles.seat_selection')}
					subtitle={restaurant.displayName || restaurant.name}
					iconLeft={<SquareIconButton variant='back' />}
					iconLeftOnClick={handleBackClick}
					iconRight={<SquareIconButton variant='exit' />}
					iconRightOnClick={handleExitClick}
				/>
			</div>

			<div className="px-4 py-5 gap-5 overflow-y-scroll flex flex-col bg-tablz-dark-gray-1 h-full">
				{isLoading || isSearching ? (
					<CarouselSkeleton />
				) : (
					matchingTables.map((table) => {
						const tableSearchResult = results?.resultMap?.[table.id];
						const available = !tableSearchResult?.['conflict'];
						const timeslotData = bulkTimeslotMap?.[table.id]?.timeSlots;
						const handleConflictNavigate = () => navigate('../modify');

						if (available) {
							return (
								<AvailableCard
									key={table.id}
									restaurant={restaurant}
									table={table}
									searchFilters={searchFilters}
									onClick={() => handleItemClick(table)}
									timeslotData={timeslotData}
									bookingType={tableSearchResult?.bookingType}
								/>
							);
						}

						const availableTimes = tableSearchResult?.availableTimes ?? [];

						if (availableTimes.length > 0) {
							const nextTime = getNextAvailableTime(searchFilters.time, availableTimes);
							return (
								<CardShell
									key={table.id}
									restaurant={restaurant}
									table={table}
									searchFilters={searchFilters}
									onCardClick={handleConflictNavigate}
									timeslotData={timeslotData}
									footer={<TimeConflictFooter nextTime={nextTime} onPickTime={handleConflictNavigate} />}
								/>
							);
						}

						return (
							<CardShell
								key={table.id}
								restaurant={restaurant}
								table={table}
								searchFilters={searchFilters}
								soldOutOverlay
								onCardClick={handleConflictNavigate}
								timeslotData={timeslotData}
								footer={
									<div className="bg-tablz-med-gray-1 flex items-center justify-end p-3 rounded-2xl text-sm">
										<button
											type="button"
											className="font-poppins font-semibold text-[color:var(--zinc-200,#e4e4e7)] cursor-pointer"
											onClick={handleConflictNavigate}
											data-testid="see-details-btn"
										>
											{t('inventory.see_details')}
										</button>
									</div>
								}
							/>
						);
					})
				)}
			</div>

			<DiscardChanges
				open={isConfirmOpen}
				onClose={() => setIsConfirmOpen(false)}
				onDiscard={handleConfirmExit}
				onCancel={handleCancelExit}
			/>

			<NoMatchingTables
				open={hasNoMatchingTables}
				onDismiss={() => {
					setHasNoMatchingTables(false);
					navigate(`/booking/${reservationId}`);
				}}
			/>
		</>
	);
};
